import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { TbDownload, TbRefresh } from "react-icons/tb";
import { PageHeaderWithBack } from "../../../../Components/Molecules";
import {
  GRID_HISTORY_COLS_6,
  GRID_HISTORY_HEADER_VALUE_CLASS,
  GridHistoryCell,
  GridHistoryContainer,
  GridHistoryHeader,
  GridHistoryRow,
} from "../../../../Components/Molecules/GridHistory";
import { getStoredSelectedAccountId, IB_ACTIVE_ACCOUNT_CHANGED_EVENT } from "../../../../Constants/accountTypes";
import useActiveIbAccount from "../../../../Hooks/useActiveIbAccount";
import { useToast } from "../../../../Hooks/toast";
import { downloadPixReceiptPdf } from "../../../../Services/downloadPixReceiptPdf";
import {
  getPixBatch,
  subscribePixBatchStream,
} from "../../../../Services/pixBatchesApi";
import { fetchOperatingAccounts } from "../../../../Services/operatingAccountsApi";
import { isIbHomeSelectableAccount } from "../../../../Utils/accountPixEligibility";
import { formatCurrency } from "../../../../Utils/formatters";

const POLL_MS = 5000;
const TERMINAL_BATCH = new Set(["completed", "failed"]);
const TERMINAL_LEG = new Set(["completed", "success", "confirmed", "accepted", "failed", "rejected"]);
const OPEN_LEG = new Set(["pending", "queued", "processing", ""]);

const formatDate = (value) => {
  if (!value) return "—";
  return new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "short" }).format(
    new Date(value),
  );
};

const normalizeStatus = (status) => String(status || "").trim().toLowerCase();

const LegStatusBadge = ({ status }) => {
  const key = normalizeStatus(status);
  const styles = {
    pending: "bg-gray-100 text-gray-600 dark:bg-brand-dark-elevated dark:text-gray-300",
    queued: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400",
    processing: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400",
    completed: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400",
    success: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400",
    confirmed: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400",
    accepted: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400",
    failed: "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-400",
    rejected: "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-400",
  };
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${styles[key] ?? styles.processing}`}
    >
      {status || "—"}
    </span>
  );
};

const eventLabel = (eventType) => {
  const key = String(eventType || "").toLowerCase();
  if (key.includes("stream.connected")) return "Ao vivo conectado";
  if (key.includes("sent")) return "Pix enviado";
  if (key.includes("confirmed")) return "Pix confirmado";
  if (key.includes("failed")) return "Pix falhou";
  if (key.includes("batch.processed")) return "Lote finalizado";
  return eventType || "Evento";
};

const eventTone = (eventType) => {
  const key = String(eventType || "").toLowerCase();
  if (key.includes("failed")) return "text-red-600 dark:text-red-400";
  if (key.includes("confirmed") || key.includes("batch.processed")) {
    return "text-emerald-600 dark:text-emerald-400";
  }
  return "text-gray-600 dark:text-gray-300";
};

export const PixLoteDetailPage = () => {
  const { batchId } = useParams();
  const location = useLocation();
  const { addToast } = useToast();
  const { account: activeIbAccount } = useActiveIbAccount();
  const queryAccountId = useMemo(() => {
    try {
      return new URLSearchParams(location.search).get("account_id") || "";
    } catch {
      return "";
    }
  }, [location.search]);
  const fallbackAccountId = activeIbAccount?.id || getStoredSelectedAccountId();
  const [resolvedAccountId, setResolvedAccountId] = useState(
    () => queryAccountId || fallbackAccountId || "",
  );
  const accountId = resolvedAccountId || queryAccountId || fallbackAccountId;
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activity, setActivity] = useState([]);
  const [streamActive, setStreamActive] = useState(false);
  const [downloadingBatch, setDownloadingBatch] = useState(false);
  const [downloadingLeg, setDownloadingLeg] = useState(null);
  const pollRef = useRef(null);
  const unsubscribeRef = useRef(null);

  const refresh = useCallback(
    async ({ silent = false } = {}) => {
      if (!batchId) {
        setBatch(null);
        setLoading(false);
        return null;
      }
      if (!silent) setLoading(true);
      try {
        const preferred = [
          queryAccountId,
          resolvedAccountId,
          fallbackAccountId,
        ].filter(Boolean);

        let lastError = null;
        for (const candidate of [...new Set(preferred)]) {
          try {
            const data = await getPixBatch(batchId, { accountId: candidate });
            const payload = data?.data ?? data;
            setResolvedAccountId(candidate);
            setBatch(payload);
            return payload;
          } catch (error) {
            lastError = error;
          }
        }

        // Last resort: scan all selectable accounts (same as list merge).
        const accounts = await fetchOperatingAccounts().catch(() => []);
        const ids = (Array.isArray(accounts) ? accounts : [])
          .filter((row) => isIbHomeSelectableAccount(row))
          .map((row) => String(row?.id || row?.account_id || "").trim())
          .filter(Boolean);
        for (const candidate of ids) {
          if (preferred.includes(candidate)) continue;
          try {
            const data = await getPixBatch(batchId, { accountId: candidate });
            const payload = data?.data ?? data;
            setResolvedAccountId(candidate);
            setBatch(payload);
            return payload;
          } catch (error) {
            lastError = error;
          }
        }

        if (!silent) {
          addToast({
            type: "error",
            title: lastError?.message || "Não foi possível carregar o lote.",
          });
        }
        return null;
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [batchId, queryAccountId, resolvedAccountId, fallbackAccountId, addToast],
  );

  const pushActivity = useCallback((event) => {
    const eventType = event?.event_type ?? event?.eventType ?? event?.type ?? "";
    const data = event?.data ?? event;
    setActivity((prev) => [
      {
        id: `${Date.now()}-${prev.length}`,
        at: new Date(),
        eventType,
        label: eventLabel(eventType),
        detail:
          data?.end_to_end_id
          || data?.end_to_end
          || data?.rejection_reason
          || data?.error
          || null,
        legIndex: data?.leg_index ?? data?.index,
      },
      ...prev,
    ].slice(0, 50));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const onAccountChanged = () => {
      refresh();
    };
    window.addEventListener(IB_ACTIVE_ACCOUNT_CHANGED_EVENT, onAccountChanged);
    return () => window.removeEventListener(IB_ACTIVE_ACCOUNT_CHANGED_EVENT, onAccountChanged);
  }, [refresh]);

  useEffect(() => {
    if (!batchId || !accountId) return undefined;

    setStreamActive(true);
    unsubscribeRef.current = subscribePixBatchStream(batchId, {
      accountId,
      onEvent: (event) => {
        const eventType = event?.event_type ?? event?.eventType ?? event?.type ?? "";
        if (String(eventType).toLowerCase().includes("stream.connected")) {
          setStreamActive(true);
          return;
        }
        if (event) pushActivity(event);
        refresh({ silent: true });
      },
      onError: () => {
        setStreamActive(false);
      },
    });

    return () => {
      unsubscribeRef.current?.();
      unsubscribeRef.current = null;
    };
  }, [batchId, accountId, pushActivity, refresh]);

  useEffect(() => {
    const status = normalizeStatus(batch?.status);
    const legsList = Array.isArray(batch?.legs) ? batch.legs : [];
    const hasOpenLegs = legsList.some((leg) => OPEN_LEG.has(normalizeStatus(leg?.status)));
    const stopPolling = TERMINAL_BATCH.has(status) && !hasOpenLegs
      || (status === "partial" && !hasOpenLegs);

    if (stopPolling) {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
      return undefined;
    }

    pollRef.current = setInterval(() => {
      refresh({ silent: true });
    }, POLL_MS);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [batch?.status, batch?.legs, refresh]);

  const legs = useMemo(
    () => (Array.isArray(batch?.legs) ? batch.legs : []),
    [batch?.legs],
  );

  const progress = useMemo(() => {
    const total = Number(batch?.leg_count) || legs.length || 0;
    const done = legs.filter((leg) => TERMINAL_LEG.has(normalizeStatus(leg?.status))).length;
    return { done, total, pct: total ? Math.min(100, Math.round((done / total) * 100)) : 0 };
  }, [batch?.leg_count, legs]);

  const handleBatchReceipt = async () => {
    if (!batchId) return;
    setDownloadingBatch(true);
    try {
      await downloadPixReceiptPdf({
        batchId,
        accountId,
        receiptPdfBase64: batch?.receipt_pdf_base64 ?? batch?.receiptPdfBase64,
      });
    } catch (error) {
      addToast({
        type: "error",
        title: error?.message || "Não foi possível baixar o comprovante do lote.",
      });
    } finally {
      setDownloadingBatch(false);
    }
  };

  const handleLegReceipt = async (leg) => {
    const paymentId = leg?.pix_id ?? leg?.pixId;
    if (!paymentId) return;
    setDownloadingLeg(paymentId);
    try {
      await downloadPixReceiptPdf({
        paymentId,
        accountId,
        fileName: `comprovante-pix-${paymentId}.pdf`,
      });
    } catch (error) {
      addToast({
        type: "error",
        title: error?.message || "Comprovante indisponível para esta parcela.",
      });
    } finally {
      setDownloadingLeg(null);
    }
  };

  return (
    <div className="pix-lote-detail min-w-0 w-full space-y-5">
      <PageHeaderWithBack
        title="Detalhe do lote Pix"
        subTitle={batchId ? `ID ${batchId}` : ""}
        backTo="/pix/lotes"
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
            {batch?.receiver_name ?? batch?.receiverName ?? "Destinatário"}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Status: {batch?.status ?? "—"}
            {streamActive ? " · ao vivo" : " · polling 5s"}
          </p>
          <div className="mt-2 h-2 w-full max-w-xs rounded-full bg-gray-100 dark:bg-brand-dark-elevated">
            <div
              className="h-2 rounded-full bg-[#e7a73f] transition-all"
              style={{ width: `${progress.pct}%` }}
            />
          </div>
          <p className="text-[11px] text-gray-400">
            {progress.done}/{progress.total} parcelas
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" className="btn-outline w-auto" onClick={() => refresh()} disabled={loading}>
            <TbRefresh className="h-4 w-4" aria-hidden />
            Atualizar
          </button>
          <button
            type="button"
            className="btn-primary w-auto"
            onClick={handleBatchReceipt}
            disabled={downloadingBatch || !batch}
          >
            <TbDownload className="h-4 w-4" aria-hidden />
            Comprovante do lote
          </button>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="summary-card">
          <p className="text-xs text-gray-500">Valor total</p>
          <p className="mt-1 text-lg font-bold tabular-nums">
            {formatCurrency(batch?.total_amount ?? batch?.totalAmount ?? 0)}
          </p>
        </div>
        <div className="summary-card">
          <p className="text-xs text-gray-500">Taxas</p>
          <p className="mt-1 text-lg font-bold tabular-nums">
            {formatCurrency(batch?.fee_total ?? batch?.feeTotal ?? 0)}
          </p>
        </div>
        <div className="summary-card">
          <p className="text-xs text-gray-500">Débito total</p>
          <p className="mt-1 text-lg font-bold tabular-nums">
            {formatCurrency(batch?.grand_total ?? batch?.grandTotal ?? 0)}
          </p>
        </div>
      </div>

      <GridHistoryContainer gridCols={GRID_HISTORY_COLS_6}>
        <GridHistoryHeader colSpan={6}>
          <span>Parcela</span>
          <span>Status</span>
          <span>E2E</span>
          <span>Erro</span>
          <span className={GRID_HISTORY_HEADER_VALUE_CLASS}>Valor</span>
          <span className="text-right">Comprovante</span>
        </GridHistoryHeader>
        {loading && !batch ? (
          <p className="col-span-6 px-4 py-8 text-center text-sm text-gray-500">Carregando…</p>
        ) : legs.length === 0 ? (
          <p className="col-span-6 px-4 py-8 text-center text-sm text-gray-500">Sem parcelas.</p>
        ) : (
          legs.map((leg) => {
            const paymentId = leg?.pix_id ?? leg?.pixId;
            const canReceipt = leg?.has_receipt || Boolean(paymentId);
            return (
              <GridHistoryRow key={leg.index ?? leg.leg_index} colSpan={6}>
                <GridHistoryCell label="Parcela">
                  <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
                    #{leg.index ?? "—"}
                  </p>
                </GridHistoryCell>
                <GridHistoryCell label="Status">
                  <LegStatusBadge status={leg.status} />
                </GridHistoryCell>
                <GridHistoryCell label="E2E">
                  <p className="truncate font-mono text-xs text-gray-500">
                    {leg.end_to_end_id ?? leg.endToEndId ?? "—"}
                  </p>
                </GridHistoryCell>
                <GridHistoryCell label="Erro">
                  <p className="text-xs text-gray-500">
                    {leg.rejection_reason ?? leg.error_code ?? "—"}
                  </p>
                </GridHistoryCell>
                <GridHistoryCell label="Valor" align="right">
                  <p className="text-sm font-semibold tabular-nums">
                    {formatCurrency(leg.amount ?? 0)}
                  </p>
                  {leg.fee ? (
                    <p className="text-xs text-gray-400">Taxa {formatCurrency(leg.fee)}</p>
                  ) : null}
                </GridHistoryCell>
                <div className="flex justify-end">
                  {canReceipt ? (
                    <button
                      type="button"
                      className="btn-outline w-auto !px-2 !py-1 text-xs"
                      disabled={downloadingLeg === paymentId}
                      onClick={() => handleLegReceipt(leg)}
                    >
                      <TbDownload className="h-3.5 w-3.5" aria-hidden />
                      PDF
                    </button>
                  ) : (
                    <span className="text-xs text-gray-400">—</span>
                  )}
                </div>
              </GridHistoryRow>
            );
          })
        )}
      </GridHistoryContainer>

      <section className="app-card p-4">
        <h2 className="text-sm font-bold text-brand-dark dark:text-gray-100">Atividade</h2>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Eventos em tempo real (enviado, confirmado, falha).
        </p>
        <ul className="mt-4 space-y-2">
          {activity.length === 0 ? (
            <li className="text-sm text-gray-500">Aguardando eventos…</li>
          ) : (
            activity.map((entry) => (
              <li
                key={entry.id}
                className="flex flex-wrap items-baseline justify-between gap-2 border-b border-gray-100 py-2 text-sm last:border-0 dark:border-brand-dark-border"
              >
                <span className={`font-medium ${eventTone(entry.eventType)}`}>{entry.label}</span>
                <span className="text-xs text-gray-400">{formatDate(entry.at)}</span>
                {entry.detail ? (
                  <span className="w-full font-mono text-xs text-gray-500">{entry.detail}</span>
                ) : null}
              </li>
            ))
          )}
        </ul>
      </section>
    </div>
  );
};
